import { getMarketHours } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import {
  getMarketCatalystChartWindow,
  getMarketCatalystDay,
} from './marketDayUtils';
import { computePriceMetrics, DATE_FORMAT, VariantDataArgs } from './types';
import { NEW_YORK_TIMEZONE } from './usePriceMarketReactionData';

interface GetCatalystDataArgs extends VariantDataArgs {
  isReportingAfterMarket: boolean;
  isMarketCatalyst?: boolean;
}

const findDayIndex = (days: string[], targetDay: string): number => {
  const directIndex = days.indexOf(targetDay);

  if (directIndex >= 0) {
    return directIndex;
  }

  return Math.max(
    days.findLastIndex(day => day <= targetDay),
    0
  );
};

export const getCatalystData = ({
  days,
  priceByDay,
  reportingDateET,
  isReportingPreMarket,
  isReportingAfterMarket,
  prevCloseHistoryData,
  isLoading,
  isMarketCatalyst = false,
}: GetCatalystDataArgs) => {
  if (isMarketCatalyst) {
    const chartDay = getMarketCatalystDay({
      days,
      priceByDay,
      reportingDateET,
      isPreMarket: isReportingPreMarket,
      isAfterMarket: isReportingAfterMarket,
    });

    const priceData = (chartDay ? (priceByDay[chartDay] ?? []) : []).filter(
      point => getMarketHours(new Date(point.time)).isMarketHours
    );

    const dayStr = chartDay ?? format(reportingDateET, DATE_FORMAT);
    const { fromDateEST, toDateEST } = getMarketCatalystChartWindow(dayStr);

    return {
      priceData,
      fromDateEST,
      toDateEST,
      ...computePriceMetrics(priceData, prevCloseHistoryData),
      isLoading,
    };
  } else {
    // Company catalyst specific logic
    const reportingDateStr = format(reportingDateET, DATE_FORMAT);
    const fromIndex = findDayIndex(days, reportingDateStr);
    const reportingDay = days[fromIndex];

    const priceData = (priceByDay[reportingDay] ?? []).filter(point => {
      const zonedTime = toZonedTime(new Date(point.time), NEW_YORK_TIMEZONE);
      const hours = zonedTime.getHours();
      const minutes = zonedTime.getMinutes();
      const afterStart = isReportingPreMarket
        ? hours >= 4
        : hours > 9 || (hours === 9 && minutes >= 30);
      const beforeClose = isReportingAfterMarket
        ? hours < 20 || (hours === 20 && minutes === 0)
        : hours < 16 || (hours === 16 && minutes === 0);
      return afterStart && beforeClose;
    });

    const fromDateEST = isReportingPreMarket
      ? new Date(`${reportingDay}T04:00:00`)
      : new Date(`${reportingDay}T09:30:00`);

    const toDateEST = isReportingAfterMarket
      ? new Date(`${reportingDay}T20:00:00`)
      : new Date(`${reportingDay}T16:00:00`);

    return {
      priceData,
      fromDateEST,
      toDateEST,
      ...computePriceMetrics(priceData, prevCloseHistoryData),
      isLoading,
    };
  }
};
